import {
  DocumentAmendment,
  ProcessedClauseChange,
} from "common/flowmaster/types/amendment";

export interface AmendmentClauseTarget {
  id?: string;
  text?: string;
  // Search strings in priority order; text stays as the legacy single anchor
  texts?: string[];
  reference?: string;
}

function trimmed(text: string | undefined): string {
  return (text ?? "").trim();
}

// The paragraph the amendment touches: before + deleted + after come from
// the same line for inline edits, so together they are a long unique snippet.
// Whole-paragraph deletes anchor on the neighbouring lines, so only the
// deleted text is on one paragraph
function paragraphSnippet(amendment: DocumentAmendment): string {
  if (amendment.action_type === "insert_after") {
    return trimmed(amendment.before_text);
  }
  if (amendment.action_type === "insert_before") {
    return trimmed(amendment.after_text);
  }
  if (
    amendment.action_type === "delete" ||
    amendment.action_type === "delete_clause"
  ) {
    return trimmed(amendment.deleted_text);
  }
  return [amendment.before_text, amendment.deleted_text, amendment.after_text]
    .map(trimmed)
    .filter(text => text !== "")
    .join(" ");
}

const MIN_WORD_CHARS = 3;
const MAX_SCROLL_TEXTS = 8;

// "." or "or" would match the first paragraph once punctuation is stripped
function isSearchable(text: string): boolean {
  return (text.match(/[\p{L}\p{N}]/gu) ?? []).length >= MIN_WORD_CHARS;
}

function unique(texts: string[]): string[] {
  const seen = new Set<string>();
  return texts.filter(text => {
    if (text === "" || seen.has(text)) {
      return false;
    }
    seen.add(text);
    return true;
  });
}

// Long snippet first, then the legacy anchor so nothing that scrolled before
// stops scrolling; added_text last since it is not in the document yet
export function getAmendmentScrollTexts(
  amendment: DocumentAmendment,
): string[] {
  const legacy = getAmendmentAnchorText(amendment);
  const fallbacks = [
    amendment.deleted_text,
    amendment.before_text,
    amendment.after_text,
    amendment.added_text,
  ]
    .map(trimmed)
    .filter(isSearchable);
  return unique([paragraphSnippet(amendment), legacy, ...fallbacks]);
}

export default function getAmendmentAnchorText(
  amendment: DocumentAmendment,
): string {
  const candidates = [
    amendment.deleted_text,
    amendment.added_text,
    amendment.before_text,
    amendment.after_text,
  ];
  return candidates.find(text => text && text.trim() !== "")?.trim() ?? "";
}

export function getClauseChangeAnchorText(
  change: ProcessedClauseChange,
): string {
  const candidates =
    change.grouped && change.related_changes
      ? change.related_changes
      : [change];
  for (const candidate of candidates) {
    const anchorText = candidate.rawAmendment
      ? getAmendmentAnchorText(candidate.rawAmendment)
      : "";
    if (anchorText) {
      return anchorText;
    }
  }
  return "";
}

// Best candidate of every related change first, then the legacy anchor so
// the cap never drops what scrolled before
export function getClauseChangeScrollTexts(
  change: ProcessedClauseChange,
): string[] {
  const candidates =
    change.grouped && change.related_changes
      ? change.related_changes
      : [change];
  const perChange = candidates.map(candidate =>
    candidate.rawAmendment
      ? getAmendmentScrollTexts(candidate.rawAmendment)
      : [],
  );
  const depth = Math.max(0, ...perChange.map(texts => texts.length));
  const byRank: string[] = [];
  for (let rank = 0; rank < depth; rank += 1) {
    perChange.forEach(texts => {
      if (texts[rank] !== undefined) {
        byRank.push(texts[rank]);
      }
    });
  }
  const legacy = getClauseChangeAnchorText(change);
  return unique([byRank[0] ?? "", legacy, ...byRank]).slice(
    0,
    MAX_SCROLL_TEXTS,
  );
}

export function getClauseChangeScrollTarget(
  change: ProcessedClauseChange,
): AmendmentClauseTarget | null {
  const text = getClauseChangeAnchorText(change);
  if (!text && !change.clause_id) {
    return null;
  }
  return {
    id: change.clause_id,
    text,
    texts: getClauseChangeScrollTexts(change),
    reference: change.clause_reference,
  };
}
